import React, { useCallback, useState, useEffect } from 'react'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import { Colors, Toaster } from 'src/components/atoms'

export interface FontSizeInputProps {
  value: number
  onChange: (value: number) => void
  disabled?: boolean
  min?: number
  max?: number
}

// 偶数で10種類のフォントサイズ
const FONT_SIZE_OPTIONS = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36]

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiAutocomplete-inputRoot': {
        padding: '0 !important',
        paddingRight: '28px !important'
      },
      '& .MuiOutlinedInput-root': {
        height: '34px',
        '& fieldset': {
          borderColor: Colors.background.silver
        },
        '&:hover fieldset': {
          borderColor: Colors.base.black
        },
        '&.Mui-focused fieldset': {
          borderColor: Colors.base.black,
          borderWidth: '1px'
        }
      },
      '& .MuiAutocomplete-input': {
        padding: '6px 12px !important',
        fontSize: '14px'
      }
    },
    option: {
      fontSize: '14px',
      minHeight: '34px',
      '&[aria-selected="true"]': {
        backgroundColor: Colors.background.lightGray
      },
      '&:hover': {
        backgroundColor: Colors.background.lightGray
      }
    },
    paper: {
      border: `1px solid ${Colors.accent.keyPurple.default}`,
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06), 0px 0px 1px rgba(0, 0, 0, 0.04)'
    }
  })
)

export const FontSizeInput = ({ value, onChange, disabled = false, min = 10, max = 72 }: FontSizeInputProps) => {
  const classes = useStyles()
  const [inputValue, setInputValue] = useState(String(value))

  // 外部からvalueが変更された場合にinputValueを同期
  useEffect(() => {
    setInputValue(String(value))
  }, [value])

  const handleChange = useCallback(
    (_event: React.ChangeEvent<{}>, newValue: number | string | null) => {
      if (newValue === null) return

      const numValue = typeof newValue === 'string' ? parseFloat(newValue) : newValue

      if (!isNaN(numValue) && numValue >= min && numValue <= max) {
        onChange(numValue)
      }
    },
    [onChange, min, max]
  )

  const handleInputChange = useCallback((_event: React.ChangeEvent<{}>, newInputValue: string) => {
    // 数字と小数点のみ許可（小数点は1つまで）
    const cleaned = newInputValue.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
    setInputValue(cleaned)
  }, [])

  const handleBlur = useCallback(() => {
    const numValue = parseFloat(inputValue)
    if (!isNaN(numValue) && numValue >= min && numValue <= max) {
      onChange(numValue)
    } else {
      // 無効な値の場合はエラーメッセージを表示して元の値に戻す
      if (!isNaN(numValue) && (numValue < min || numValue > max)) {
        Toaster.error(`文字サイズは${min}〜${max}pxの範囲で入力してください`)
      }
      setInputValue(String(value))
    }
  }, [inputValue, onChange, value, min, max])

  return (
    <Autocomplete
      freeSolo
      disableClearable
      forcePopupIcon
      options={FONT_SIZE_OPTIONS}
      value={value}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      disabled={disabled}
      getOptionLabel={option => String(option)}
      renderOption={option => `${option}px`}
      classes={{
        root: classes.root,
        option: classes.option,
        paper: classes.paper
      }}
      renderInput={params => (
        <TextField {...params} variant="outlined" size="small" placeholder="px" InputProps={{ ...params.InputProps }} />
      )}
    />
  )
}
